import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiBarChart2, FiBriefcase, FiClipboard, FiPieChart } from 'react-icons/fi';

import CombinedClientScopeFilters from '../combined-reports/CombinedClientScopeFilters';
import useCombinedReportScope from '../combined-reports/useCombinedReportScope';
import { fetchCombinedOverview, resolveCombinedReportError } from '../combined-reports/combinedReportUtils';
import './ConsolidatedReports.css';

const apiUrl = process.env.REACT_APP_API_URL;

const formatAmount = (value) => `₹ ${Number(value ?? 0).toLocaleString('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})}`;

const formatDate = (value) => {
  if (!value) {
    return '-';
  }

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Kolkata',
  }).format(new Date(value));
};

const toSectionTotals = (sectionMap) => Object.entries(sectionMap || {})
  .map(([label, values]) => ({
    label,
    amount: Object.values(values || {}).reduce((sum, amount) => sum + Number(amount ?? 0), 0),
  }))
  .sort((left, right) => Math.abs(right.amount) - Math.abs(left.amount));

const ConsolidatedReports = () => {
  const {
    financialYear,
    clientOptions,
    clientsLoading,
    clientScopeMode,
    selectedClientIds,
    effectiveClientIds,
    scopeSummary,
    selectionPreview,
    onScopeModeChange,
    onToggleClientId,
  } = useCombinedReportScope();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reportData, setReportData] = useState({
    profitLoss: null,
    balanceSheet: null,
    taxComputation: null,
    capitalGains: null,
  });

  useEffect(() => {
    if (!financialYear || effectiveClientIds.length === 0) {
      setReportData({
        profitLoss: null,
        balanceSheet: null,
        taxComputation: null,
        capitalGains: null,
      });
      setError('');
      return;
    }

    let ignore = false;
    setLoading(true);

    fetchCombinedOverview(apiUrl, effectiveClientIds, financialYear, {
      allAccessibleClients: clientScopeMode === 'all',
    })
      .then((combinedData) => {
        if (ignore) {
          return;
        }

        setReportData(combinedData);
        setError('');
      })
      .catch((requestError) => {
        if (ignore) {
          return;
        }

        setError(resolveCombinedReportError(requestError, 'Unable to load the combined overview.'));
      })
      .finally(() => {
        if (!ignore) {
          setLoading(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, [clientScopeMode, effectiveClientIds, financialYear]);

  const revenueTotals = useMemo(
    () => toSectionTotals(reportData.profitLoss?.Revenue).slice(0, 4),
    [reportData.profitLoss],
  );

  const expenseTotals = useMemo(
    () => toSectionTotals(reportData.profitLoss?.Expenses).slice(0, 4),
    [reportData.profitLoss],
  );

  const assetTotals = useMemo(
    () => toSectionTotals(reportData.balanceSheet?.Assets).slice(0, 4),
    [reportData.balanceSheet],
  );

  const liabilityTotals = useMemo(
    () => toSectionTotals(reportData.balanceSheet?.Liabilities).slice(0, 4),
    [reportData.balanceSheet],
  );

  const taxSummaryRows = useMemo(() => {
    const tax = reportData.taxComputation;
    if (!tax) {
      return [];
    }

    return [
      { label: 'Business Income After Tax Depreciation', amount: tax.businessIncomeAfterTaxDepreciation },
      { label: 'Tax Depreciation', amount: tax.taxDepreciation },
      { label: 'Dividend Income', amount: tax.dividendIncome },
      { label: 'Interest Income', amount: tax.interestIncome },
      { label: 'Other Sources Income', amount: tax.otherSourcesIncome },
      { label: 'Short-Term Capital Gain', amount: tax.shortTermCapitalGain },
      { label: 'Taxable Long-Term Capital Gain', amount: tax.longTermCapitalGain },
      { label: 'Gross Total Income', amount: tax.grossTotalIncome, highlight: true },
    ];
  }, [reportData.taxComputation]);

  const capitalGainRows = useMemo(() => {
    const capitalGains = reportData.capitalGains;
    if (!capitalGains) {
      return [];
    }

    return [
      ...(capitalGains.ltcg || []).map((row) => ({ ...row, gainType: 'LTCG' })),
      ...(capitalGains.stcg || []).map((row) => ({ ...row, gainType: 'STCG' })),
    ]
      .sort((left, right) => new Date(right.transferDate || 0).getTime() - new Date(left.transferDate || 0).getTime())
      .slice(0, 6);
  }, [reportData.capitalGains]);

  const headlineCards = useMemo(() => {
    const profitLoss = reportData.profitLoss;
    const balanceSheet = reportData.balanceSheet;
    const tax = reportData.taxComputation;
    const capitalGains = reportData.capitalGains;

    return [
      { label: 'Total Revenue', value: formatAmount(profitLoss?.totalRevenue) },
      { label: 'Gross Total Income', value: formatAmount(tax?.grossTotalIncome), accent: true },
      { label: 'Total Assets', value: formatAmount(balanceSheet?.totalAssets) },
      {
        label: 'Taxable Capital Gains',
        value: formatAmount(Number(capitalGains?.stcgTaxableTotal ?? 0) + Number(capitalGains?.ltcgTaxableTotal ?? 0)),
      },
    ];
  }, [reportData.balanceSheet, reportData.capitalGains, reportData.profitLoss, reportData.taxComputation]);

  if (!financialYear) {
    return (
      <div className="consolidated-reports-page">
        <section className="consolidated-reports-hero consolidated-reports-hero--empty">
          <div>
            <p className="consolidated-reports-eyebrow">Combined Reports</p>
            <h1>Start with a financial year.</h1>
            <p>Select the year from the header, then choose whether the combined section should include all accessible client IDs or only the selected ones.</p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="consolidated-reports-page">
      <section className="consolidated-reports-hero">
        <div>
          <p className="consolidated-reports-eyebrow">Combined Reports</p>
          <h1>One view across accounting, capital gains, and tax for all or selected clients.</h1>
          <p>Use this overview to scan the combined position first, then move into the dedicated combined P&amp;L, balance sheet, capital gains, or tax pages only where follow-up is needed.</p>
        </div>
        <div className="consolidated-reports-context-card">
          <div className="consolidated-reports-context-card__row">
            <span>Client Scope</span>
            <strong>{scopeSummary}</strong>
          </div>
          <div className="consolidated-reports-context-card__row">
            <span>Financial Year</span>
            <strong>{financialYear}</strong>
          </div>
          <div className="consolidated-reports-context-card__row">
            <span>Included IDs</span>
            <strong>{selectionPreview}</strong>
          </div>
          <div className="consolidated-reports-context-card__row">
            <span>Sections Included</span>
            <strong>P&amp;L, CG, Balance Sheet, Tax</strong>
          </div>
        </div>
      </section>

      <CombinedClientScopeFilters
        clientOptions={clientOptions}
        clientsLoading={clientsLoading}
        clientScopeMode={clientScopeMode}
        selectedClientIds={selectedClientIds}
        effectiveClientIds={effectiveClientIds}
        financialYear={financialYear}
        onScopeModeChange={onScopeModeChange}
        onToggleClientId={onToggleClientId}
      />

      {loading && <p className="financial-message">Loading consolidated reports...</p>}
      {error && <p className="financial-message financial-message--error">{error}</p>}
      {!loading && !error && clientOptions.length > 0 && effectiveClientIds.length === 0 ? (
        <p className="financial-message">Choose one or more client IDs to load the combined overview.</p>
      ) : null}

      <section className="consolidated-reports-stat-grid">
        {headlineCards.map((card) => (
          <article key={card.label} className={`consolidated-reports-stat-card${card.accent ? ' consolidated-reports-stat-card--accent' : ''}`}>
            <span>{card.label}</span>
            <strong>{card.value}</strong>
          </article>
        ))}
      </section>

      <section className="consolidated-reports-panel-grid">
        <article className="consolidated-panel">
          <div className="consolidated-panel__header">
            <div className="consolidated-panel__title-wrap">
              <span className="consolidated-panel__icon"><FiBarChart2 /></span>
              <div>
                <h2>Profit &amp; Loss Snapshot</h2>
                <p>Compare revenue and expense blocks without opening the full combined statement.</p>
              </div>
            </div>
            <Link to="/combined-profit-loss" className="consolidated-panel__link">Combined P&amp;L <FiArrowRight /></Link>
          </div>

          <div className="consolidated-panel__totals-grid">
            <div>
              <span>Total Revenue</span>
              <strong>{formatAmount(reportData.profitLoss?.totalRevenue)}</strong>
            </div>
            <div>
              <span>Total Expense</span>
              <strong>{formatAmount(reportData.profitLoss?.totalExpense)}</strong>
            </div>
          </div>

          <div className="consolidated-panel__split">
            <div>
              <h3>Top Revenue Blocks</h3>
              <ul className="consolidated-list">
                {revenueTotals.map((item) => (
                  <li key={item.label}><span>{item.label}</span><strong>{formatAmount(item.amount)}</strong></li>
                ))}
              </ul>
            </div>
            <div>
              <h3>Top Expense Blocks</h3>
              <ul className="consolidated-list">
                {expenseTotals.map((item) => (
                  <li key={item.label}><span>{item.label}</span><strong>{formatAmount(item.amount)}</strong></li>
                ))}
              </ul>
            </div>
          </div>
        </article>

        <article className="consolidated-panel">
          <div className="consolidated-panel__header">
            <div className="consolidated-panel__title-wrap">
              <span className="consolidated-panel__icon"><FiPieChart /></span>
              <div>
                <h2>Balance Sheet Snapshot</h2>
                <p>Review the closing structure of assets and liabilities before opening the combined statement.</p>
              </div>
            </div>
            <Link to="/combined-balance-sheet" className="consolidated-panel__link">Combined balance sheet <FiArrowRight /></Link>
          </div>

          <div className="consolidated-panel__totals-grid">
            <div>
              <span>Total Assets</span>
              <strong>{formatAmount(reportData.balanceSheet?.totalAssets)}</strong>
            </div>
            <div>
              <span>Total Liabilities</span>
              <strong>{formatAmount(reportData.balanceSheet?.totalLiabilities)}</strong>
            </div>
          </div>

          <div className="consolidated-panel__split">
            <div>
              <h3>Asset Blocks</h3>
              <ul className="consolidated-list">
                {assetTotals.map((item) => (
                  <li key={item.label}><span>{item.label}</span><strong>{formatAmount(item.amount)}</strong></li>
                ))}
              </ul>
            </div>
            <div>
              <h3>Liability Blocks</h3>
              <ul className="consolidated-list">
                {liabilityTotals.map((item) => (
                  <li key={item.label}><span>{item.label}</span><strong>{formatAmount(item.amount)}</strong></li>
                ))}
              </ul>
            </div>
          </div>
        </article>

        <article className="consolidated-panel">
          <div className="consolidated-panel__header">
            <div className="consolidated-panel__title-wrap">
              <span className="consolidated-panel__icon"><FiBriefcase /></span>
              <div>
                <h2>Capital Gain Snapshot</h2>
                <p>Track gross and taxable capital gains and inspect the most recent combined gain rows in one place.</p>
              </div>
            </div>
            <Link to="/combined-capital-gains" className="consolidated-panel__link">Combined capital gains <FiArrowRight /></Link>
          </div>

          <div className="consolidated-panel__totals-grid consolidated-panel__totals-grid--four">
            <div>
              <span>Gross LTCG</span>
              <strong>{formatAmount(reportData.capitalGains?.ltcgTotal)}</strong>
            </div>
            <div>
              <span>Taxable LTCG</span>
              <strong>{formatAmount(reportData.capitalGains?.ltcgTaxableTotal)}</strong>
            </div>
            <div>
              <span>Gross STCG</span>
              <strong>{formatAmount(reportData.capitalGains?.stcgTotal)}</strong>
            </div>
            <div>
              <span>Taxable STCG</span>
              <strong>{formatAmount(reportData.capitalGains?.stcgTaxableTotal)}</strong>
            </div>
          </div>

          <div className="consolidated-mini-table-wrap">
            <table className="consolidated-mini-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Asset</th>
                  <th>Transfer Date</th>
                  <th>Gain</th>
                </tr>
              </thead>
              <tbody>
                {capitalGainRows.length > 0 ? capitalGainRows.map((row, index) => (
                  <tr key={`${row.gainType}-${row.assetName}-${index}`}>
                    <td>{row.gainType}</td>
                    <td>{row.assetName}</td>
                    <td>{formatDate(row.transferDate)}</td>
                    <td>{formatAmount(row.gainAmount)}</td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="4">No capital gain rows available for the selected financial year.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </article>

        <article className="consolidated-panel">
          <div className="consolidated-panel__header">
            <div className="consolidated-panel__title-wrap">
              <span className="consolidated-panel__icon"><FiClipboard /></span>
              <div>
                <h2>Tax Computation Snapshot</h2>
                <p>See taxable income, depreciation effect, and capital-gain tax posture from the combined tax report.</p>
              </div>
            </div>
            <Link to="/combined-tax-computation" className="consolidated-panel__link">Combined tax computation <FiArrowRight /></Link>
          </div>

          <ul className="consolidated-list consolidated-list--dense">
            {taxSummaryRows.map((row) => (
              <li key={row.label} className={row.highlight ? 'consolidated-list__item--highlight' : ''}>
                <span>{row.label}</span>
                <strong>{formatAmount(row.amount)}</strong>
              </li>
            ))}
          </ul>
        </article>
      </section>
    </div>
  );
};

export default ConsolidatedReports;